const POST_AUTH_RETURN_KEY = 'luma_post_auth_return';
const POST_AUTH_RETURN_MAX_AGE_MS = 30 * 60 * 1000;

type LocationLike = Pick<Location, 'hash' | 'pathname'>;

function normalizedInvitationReturn(candidate: string | null): string | null {
  if (!candidate) return null;
  try {
    const url = new URL(candidate, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname !== '/voice-invitation' || url.search) {
      return null;
    }
    const token = new URLSearchParams(url.hash.slice(1)).get('token') || '';
    if (!token || token.length > 512 || !/^[A-Za-z0-9_-]+$/.test(token)) return null;
    return `/voice-invitation#token=${encodeURIComponent(token)}`;
  } catch {
    return null;
  }
}

export function currentInvitationReturn(location: LocationLike = window.location): string | null {
  return normalizedInvitationReturn(`${location.pathname}${location.hash}`);
}

export function rememberCurrentAuthReturn(location: LocationLike = window.location): string | null {
  const destination = currentInvitationReturn(location);
  try {
    if (destination) {
      sessionStorage.setItem(POST_AUTH_RETURN_KEY, JSON.stringify({
        destination,
        createdAt: Date.now(),
      }));
    }
  } catch {
    // OAuth still works if storage is unavailable; the user simply returns home.
  }
  return destination;
}

export function readPostAuthReturn(): string {
  let candidate: string | null = null;
  try {
    const stored = sessionStorage.getItem(POST_AUTH_RETURN_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as { destination?: unknown; createdAt?: unknown };
      const age = typeof parsed.createdAt === 'number' ? Date.now() - parsed.createdAt : -1;
      if (
        typeof parsed.destination === 'string'
        && age >= 0
        && age <= POST_AUTH_RETURN_MAX_AGE_MS
      ) {
        candidate = parsed.destination;
      } else {
        sessionStorage.removeItem(POST_AUTH_RETURN_KEY);
      }
    }
  } catch {
    try {
      sessionStorage.removeItem(POST_AUTH_RETURN_KEY);
    } catch {
      // Storage is unavailable, so there is nothing else to clean up.
    }
    return '/';
  }
  const destination = normalizedInvitationReturn(candidate);
  if (!destination && candidate) clearPostAuthReturn();
  return destination || '/';
}

export function clearPostAuthReturn(): void {
  try {
    sessionStorage.removeItem(POST_AUTH_RETURN_KEY);
  } catch {
    // The invitation route is already restored even if storage cleanup fails.
  }
}
